import React from "react";

import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";

export default function PrivacyPolicy({
    lastUpdated = new Date().toLocaleDateString(),
}) {
    return (
        <Box sx={{ p: 3, maxWidth: 900, mx: "auto", lineHeight: 1.7 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
                PRIVACY POLICY
            </Typography>

            <Typography variant="h6" fontWeight={600} gutterBottom>
                GroomWell Digital Services
            </Typography>

            <Typography variant="subtitle2" gutterBottom>
                Last Updated:
            </Typography>
            <Typography variant="body2">{lastUpdated}</Typography>
            <Typography paragraph>
                GroomWell Digital Services (“GroomWell”, “We”, “Us”, “Our”) operates a digital platform that
                allows salons to register, manage their services, and accept appointments from clients
                (“Users”, “You”, “Your”). This Privacy Policy explains how we collect, use, share, and
                protect your personal information when you use our platform, website, or mobile services.
            </Typography>

            <Typography paragraph>
                By accessing or using GroomWell, you agree to the terms described in this Privacy Policy.
                If you do not agree, please do not use the Platform.
            </Typography>

            {/* 1. Information We Collect */}
            <Typography variant="h5" fontWeight={600} mt={3}>
                1. Information We Collect
            </Typography>

            <Typography variant="h6" fontWeight={600} mt={2}>
                1.1 Information You Provide to Us
            </Typography>

            <Typography fontWeight={600} mt={1}>
                For Salon Clients
            </Typography>
            <ul>
                <li>Full name</li>
                <li>Phone number</li>
                <li>Email address</li>
                <li>Account login information</li>
                <li>Appointment details</li>
                <li>Reviews and ratings</li>
            </ul>

            <Typography fontWeight={600} mt={2}>
                For Salon Owners
            </Typography>
            <ul>
                <li>Full name</li>
                <li>Phone number</li>
                <li>Email address</li>
                <li>Salon name and business details</li>
                <li>Business address and location</li>
                <li>Services, prices, images, and descriptions</li>
                <li>Bank or UPI details for subscription payments (if provided)</li>
            </ul>

            <Typography variant="h6" fontWeight={600} mt={3}>
                1.2 Automatically Collected Information
            </Typography>
            <ul>
                <li>IP address</li>
                <li>Device information</li>
                <li>Browser type</li>
                <li>Operating system</li>
                <li>Access times and dates</li>
                <li>Pages visited</li>
                <li>Usage patterns and interaction data</li>
            </ul>
            <Typography paragraph>
                We may use cookies and similar technologies to improve platform performance and personalize
                your experience.
            </Typography>

            <Typography variant="h6" fontWeight={600} mt={3}>
                1.3 Third Party Information
            </Typography>
            <Typography paragraph>
                We may receive information from payment gateways (limited to transaction status), analytics
                tools, or social login providers. We never receive or store your card details.
            </Typography>

            {/* 2. How We Use Your Information */}
            <Typography variant="h5" fontWeight={600} mt={4}>
                2. How We Use Your Information
            </Typography>

            <Typography fontWeight={600} mt={2}>
                2.1 For Platform Functionality
            </Typography>
            <ul>
                <li>Create and manage user accounts</li>
                <li>Facilitate booking between clients and salons</li>
                <li>Enable salons to manage their listings and schedules</li>
                <li>Send appointment confirmations, reminders, and updates</li>
            </ul>

            <Typography fontWeight={600} mt={2}>
                2.2 For Business Operations
            </Typography>
            <ul>
                <li>Improve website performance and user experience</li>
                <li>Provide customer support</li>
                <li>Prevent fraud and abuse</li>
                <li>Monitor platform usage</li>
                <li>Generate aggregated analytics (never showing personal data)</li>
            </ul>

            <Typography fontWeight={600} mt={2}>
                2.3 For Legal Compliance
            </Typography>
            <ul>
                <li>Verify business eligibility</li>
                <li>Maintain records for tax or regulatory requirements</li>
                <li>Respond to lawful requests from authorities</li>
            </ul>

            {/* 3. Sharing of Information */}
            <Typography variant="h5" fontWeight={600} mt={4}>
                3. Sharing of Information
            </Typography>

            <Typography paragraph>We do not sell or rent your personal data.</Typography>

            <Typography fontWeight={600} mt={2}>
                3.1 With Salon Owners
            </Typography>
            <Typography paragraph>
                If you book an appointment, your essential contact details will be shared with the salon for
                service fulfillment.
            </Typography>

            <Typography fontWeight={600} mt={2}>
                3.2 With Service Providers
            </Typography>
            <Typography paragraph>
                We may share limited information with hosting providers, payment gateways, messaging
                services, analytics tools, etc. These partners are required to protect your data.
            </Typography>

            <Typography fontWeight={600} mt={2}>
                3.3 For Legal Reasons
            </Typography>
            <Typography paragraph>
                We may share information if required by law, law enforcement, or court orders.
            </Typography>

            <Typography fontWeight={600} mt={2}>
                3.4 Business Transfers
            </Typography>
            <Typography paragraph>
                If GroomWell undergoes acquisition or restructuring, your data may be transferred with the
                same protections.
            </Typography>

            {/* 4. Data Protection */}
            <Typography variant="h5" fontWeight={600} mt={4}>
                4. Data Protection and Security
            </Typography>
            <Typography paragraph>
                We use industry standard measures like HTTPS, secure servers, encryption, and restricted
                access. No system is 100 percent secure, but we take all reasonable measures.
            </Typography>

            {/* 5. Rights */}
            <Typography variant="h5" fontWeight={600} mt={4}>
                5. Your Rights
            </Typography>
            <Typography paragraph>
                You may request access, correction, deletion, restriction, or a copy of your personal data
                by emailing: <b>support@groomwell.in</b>
            </Typography>

            {/* 6. Retention */}
            <Typography variant="h5" fontWeight={600} mt={4}>
                6. Data Retention
            </Typography>
            <Typography paragraph>
                We retain your information while your account is active, for legal reasons, or until you
                request deletion.
            </Typography>

            {/* 7. Cookies */}
            <Typography variant="h5" fontWeight={600} mt={4}>
                7. Cookies and Tracking
            </Typography>
            <Typography paragraph>
                We use cookies for sessions, analytics, performance, and personalization. Disabling cookies
                may affect functionality.
            </Typography>

            {/* 8. Children */}
            <Typography variant="h5" fontWeight={600} mt={4}>
                8. Children’s Privacy
            </Typography>
            <Typography paragraph>
                GroomWell is not intended for individuals under 18. We do not knowingly collect data from
                minors.
            </Typography>

            {/* 9. Links */}
            <Typography variant="h5" fontWeight={600} mt={4}>
                9. Third Party Links
            </Typography>
            <Typography paragraph>
                External links on our platform may lead to third party websites. We are not responsible for
                their privacy practices.
            </Typography>

            {/* 10. Changes */}
            <Typography variant="h5" fontWeight={600} mt={4}>
                10. Changes to This Privacy Policy
            </Typography>
            <Typography paragraph>
                We may update this policy occasionally. Continued use of the platform means you accept the
                updated terms.
            </Typography>

            {/* 11. Contact */}
            <Typography variant="h5" fontWeight={600} mt={4}>
                11. Contact Us
            </Typography>
            <Typography paragraph>
                For questions, concerns, or complaints:
                <br />📧 <b>support@groomwell.in</b>
                <br />📍 India
            </Typography>
        </Box>
    );
}
PrivacyPolicy.propTypes = {
    lastUpdated: PropTypes.string

};